import PropTypes from 'prop-types';

import CloseIcon from '@mui/icons-material/Close';
import MaximizeIcon from '@mui/icons-material/Maximize';
import MinimizeIcon from '@mui/icons-material/Minimize';
import { DialogTitle, IconButton, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

import Tooltip from '../Tooltip';

const StyledDialogTitle = styled(DialogTitle)(() => ({
  display: 'flex',
  alignItems: 'center',
  height: 'auto',
  padding: '1rem',
  lineHeight: '1',
  backgroundColor: '#fff',
}));

const StyledTitle = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isMaxWidthIncludes',
})(({ theme, isMaxWidthIncludes }) => ({
  textTransform: 'uppercase',
  flex: '1',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  wordBreak: 'break-word',
  whiteSpace: 'break-spaces',
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: '2',
  [theme.breakpoints.down('sm')]: {
    fontSize: '2rem',
  },
  ...(isMaxWidthIncludes && { maxWidth: '200px' }),
}));

export const MinimizedModalTitle = ({
  children,
  onClose,
  onCloseRef = null,
  setIsModalMinimized,
  progressValue = 0,
}) => (
  <StyledDialogTitle>
    <StyledTitle
      variant="h5"
      color="primary"
      isMaxWidthIncludes
      style={{
        background: progressValue
          ? `linear-gradient(to right,  #aacfdd 0%,#aacfdd ${progressValue}%,#ffffff ${progressValue}%,#ffffff 100%)`
          : '#fff',
      }}
      component="div"
    >
      {children}
    </StyledTitle>
    <Tooltip tooltip="Restore">
      <IconButton
        onClick={() => setIsModalMinimized(false)}
        aria-label="Restore"
        size="small"
      >
        <MaximizeIcon />
      </IconButton>
    </Tooltip>
    {onClose ? (
      <IconButton
        onClick={onClose}
        aria-label="Close"
        size="small"
        ref={onCloseRef}
      >
        <CloseIcon />
      </IconButton>
    ) : null}
  </StyledDialogTitle>
);

MinimizedModalTitle.propTypes = {
  setIsModalMinimized: PropTypes.func.isRequired,
  progressValue: PropTypes.number,
  onCloseRef: PropTypes.instanceOf(Object),
  onClose: PropTypes.func,
  children: PropTypes.node,
};

const ModalTitle = ({
  children,
  onClose,
  onCloseRef = null,
  isMinimizeAvailable,
  setIsModalMinimized,
  customTitleStyle,
}) => (
  <DialogTitle
    sx={{
      display: 'flex',
      alignItems: 'center',
      height: '7rem',
      padding: '2rem',
    }}
  >
    <StyledTitle
      variant="h5"
      color="primary"
      style={customTitleStyle}
      component="div"
    >
      {children}
    </StyledTitle>
    {isMinimizeAvailable ? (
      <Tooltip tooltip="Minimize">
        <IconButton
          onClick={() => setIsModalMinimized(true)}
          aria-label="Minimize"
          size="large"
        >
          <MinimizeIcon />
        </IconButton>
      </Tooltip>
    ) : null}
    {onClose ? (
      <IconButton
        onClick={onClose}
        aria-label="Close"
        size="large"
        ref={onCloseRef}
      >
        <CloseIcon />
      </IconButton>
    ) : null}
  </DialogTitle>
);

ModalTitle.propTypes = {
  setIsModalMinimized: PropTypes.func,
  isMinimizeAvailable: PropTypes.bool,
  onCloseRef: PropTypes.instanceOf(Object),
  onClose: PropTypes.func,
  children: PropTypes.node,
  customTitleStyle: PropTypes.instanceOf(Object),
};

export default ModalTitle;
